/**
 * *Ask ChatGPT* from the coach sheet: the position written out as a prompt,
 * and the link that opens ChatGPT with it already typed.
 *
 * The coach's simulations say *what* scores best; a conversation is better at
 * *why*, and at the follow-up question. The prompt carries only what this
 * player can see — the same redacted view the coach searched from — plus the
 * coach's own numbers, so ChatGPT can agree or argue with them.
 *
 * `chatgpt.com/?q=` fills the composer on the web. The ChatGPT phone app
 * ignores it, so the sheet also copies the prompt to paste.
 */
import { cardLabel, getGame, type CardId, type Seat } from '@cards/shared'
import { readCalls } from '../games/common'
import { contractorSeatOf, type TableView } from '../table/view'
import type { CoachAdvice } from './protocol'
import { adviceHeadline } from './useCoach'

/** The link that opens a new ChatGPT chat with `prompt` in the composer. */
export function chatgptUrl(prompt: string): string {
  const url = new URL('https://chatgpt.com/')
  url.searchParams.set('q', prompt)
  return url.toString()
}

const cards = (list: readonly CardId[]): string => (list.length > 0 ? list.map(cardLabel).join(' ') : '(none)')

function signed(n: number): string {
  const v = Math.abs(n) >= 10 ? Math.round(n) : Math.round(n * 10) / 10
  return `${v > 0 ? '+' : ''}${v}`
}

/** Everything a coach would need to talk this decision through, as plain text. */
export function coachPrompt(view: TableView, advice: CoachAdvice): string {
  const match = view.match
  const hand = view.hand
  const me = view.mySeat
  if (!match || !hand || me === null) return ''
  const mod = getGame(match.gameId)
  const config = match.config
  const table = match.table
  const lines: string[] = []

  lines.push(
    `I'm playing ${view.gameUi.name} (the card game) and I'd like you to coach me through my next decision.`,
    '',
  )

  // Rules as the host set them, in the lobby's own words.
  const rules: string[] = []
  const bag = (config ?? {}) as Record<string, unknown>
  for (const option of view.gameUi.options ?? []) {
    const want = JSON.stringify(bag[option.key])
    const choice = option.choices.find((c) => JSON.stringify(c.value) === want)
    if (choice) rules.push(`${option.label}: ${choice.label}`)
  }
  if (rules.length > 0) lines.push(`Rules: ${rules.join('; ')}.`)

  // Who sits where, relative to me. Play order is seat order.
  const where: Record<string, string> = { w: 'on my left', e: 'on my right', n: 'across from me' }
  const who = (seat: Seat | null): string => {
    if (seat === null) return '?'
    if (seat === me) return 'Me'
    return view.name(seat)
  }
  lines.push(`Players, in turn order (play passes ${view.gameUi.rotation}):`)
  for (const s of view.seats) {
    const role = s.isMe ? 'this is me' : s.isPartner ? 'my partner' : 'opponent'
    const place = !s.isMe && view.seatCount === 4 ? `, ${where[s.position] ?? ''}` : ''
    const dealer = s.seat === hand.dealer ? ', dealer' : ''
    const sitting = hand.activeSeats.includes(s.seat) ? '' : ', sitting this hand out'
    lines.push(`- ${s.name}: ${role}${place}${dealer}${sitting}`)
  }

  // The score, by side.
  const sides = table.teams.map((t) => {
    const names = t.seats.map((s) => (s === me ? 'me' : view.name(s))).join(' & ')
    return `${names} ${match.scores[t.id] ?? 0}`
  })
  lines.push(`Score: ${sides.join(', ')}. This is hand ${hand.index + 1}.`)
  lines.push('')

  // The auction, call by call.
  const calls = readCalls(hand.auction)
  if (calls.length > 0) {
    const said = calls.map(({ seat, call }) => {
      let label: string
      try {
        label = mod.auction.describe(call as never, config).label
      } catch {
        label = '?'
      }
      return `${who(seat)} ${label}`
    })
    lines.push(`Bidding so far: ${said.join(', ')}.`)
  }
  if (hand.contract) {
    const by = contractorSeatOf(hand.contract, table.seatCount)
    const side = by === null ? '' : by === me ? ' (me)' : table.teamOf[by] === table.teamOf[me] ? ' (my side)' : ' (opponents)'
    lines.push(`Contract: ${mod.contractLabel(hand.contract, config, hand.extra)}${by === null ? '' : ` by ${who(by)}${side}`}.`)
  }

  const kittyName = view.gameUi.kittyName ?? 'Kitty'
  if (hand.kitty.kind === 'cards' && hand.kitty.cards.length > 0) lines.push(`${kittyName} (face up): ${cards(hand.kitty.cards)}.`)
  if (hand.discards.kind === 'cards' && hand.discards.cards.length > 0) lines.push(`Discarded: ${cards(hand.discards.cards)}.`)

  // The play, trick by trick.
  const played = hand.tricks.filter((t) => t.plays.length > 0)
  if (played.length > 0) {
    lines.push('Tricks so far:')
    for (const t of played) {
      const plays = t.plays.map((p) => `${who(p.seat)} ${cardLabel(p.card)}`).join(', ')
      const end = t.winner !== null ? ` — won by ${who(t.winner)}` : ' — in progress'
      lines.push(`${t.index + 1}. ${plays}${end}`)
    }
  }
  lines.push('')

  // What everybody holds, as far as I can see.
  const mine = view.gameUi.sortHand ? view.gameUi.sortHand(view, view.myCards) : view.myCards
  lines.push(`My hand: ${cards(mine)}.`)
  for (const s of view.seats) {
    if (s.isMe || !s.holding) continue
    if (s.holding.kind === 'cards') lines.push(`${s.name}'s hand (face up): ${cards(s.holding.cards)}.`)
    else lines.push(`${s.name} holds ${s.holding.count} card${s.holding.count === 1 ? '' : 's'}.`)
  }
  lines.push('')

  // The decision in front of me.
  const actions = view.actions
  const acting = actions?.toAct ?? me
  const forSeat = acting !== me ? ` (playing for ${view.name(acting)})` : ''
  if (actions?.pending && actions.controls.includes(actions.pending.seat)) {
    lines.push(`It's my decision${forSeat}: ${actions.pending.kind.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}.`)
  } else if (actions && actions.legalPlays.length > 0) {
    const legal = [...new Set(actions.legalPlays.map((p) => cardLabel(p.card)))]
    lines.push(`It's my turn to play${forSeat}. Legal cards: ${legal.join(' ')}.`)
  } else if (actions && actions.legalCalls.length > 0) {
    lines.push(`It's my turn to bid${forSeat}. Legal calls: ${actions.legalCalls.map((c) => c.label).join(', ')}.`)
  }

  // What the app's coach made of it.
  lines.push('')
  lines.push(`The app's coach suggests: ${adviceHeadline(view, advice.action)}. Its reasoning: ${advice.reason}`)
  if (advice.ranked.length > 1) {
    lines.push(
      `It simulated ${advice.worlds} deals consistent with what I can see and played each option out to the end of the hand. Average result per option (points for my side), and how often each was best:`,
    )
    for (const c of advice.ranked) {
      const nominate = c.nominate ? ` naming ${c.nominate}` : ''
      lines.push(`- ${c.label}${nominate}: ${signed(c.mean)} pts, best in ${Math.round(c.bestShare * 100)}%`)
    }
  }

  lines.push(
    '',
    'Please coach me: what are my real options here, what would a strong player do, and why? Walk through what the bidding and the cards played so far tell me about the hidden hands, and the principle behind the choice so I can apply it next time. Say whether you agree with the app\'s suggestion, and when the alternative would be better. Keep it concise.',
  )
  return lines.join('\n')
}
